import { useEffect, useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { DownloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import { Button, DatePicker, Select, TableSearch, Tooltip } from '@/components/ui'

import {
  type ActivityLogFilterOptions,
  type ActivityLogFilters,
  activityLogQueryParams,
  validateExportRange,
} from './types'

const { RangePicker } = DatePicker

export interface ActivityLogFilterBarProps {
  /** Index path of the surface, e.g. `/admin/activity_logs`. Export posts to `${basePath}/export`. */
  basePath: string
  /** Filters echoed back by the controller; seeds the draft state. */
  filters: ActivityLogFilters
  filterOptions: ActivityLogFilterOptions
  /**
   * Admin surface only. `Events::ActivityLogsController` scopes its rows to one
   * event structurally and strips :event_id from the filters it permits, so the
   * event portal has nothing to choose between.
   */
  showEventFilter?: boolean
  /** Carried through so filtering does not reset the chosen page size. */
  perPage?: number
  className?: string
}

/**
 * Filter toolbar for both activity log viewers: search, date range and — on the
 * admin surface — the event.
 *
 * Filters are drafted locally and submitted with "Apply Filters" (matching the email
 * activity report), so changing two of them costs one round trip instead of two. The
 * CSV export hard-navigates out of Inertia — it streams a file rather than a page — and
 * stays disabled until the date range satisfies the server's 90-day export rule.
 */
export function ActivityLogFilterBar({
  basePath,
  filters,
  filterOptions,
  showEventFilter = false,
  perPage,
  className = '',
}: ActivityLogFilterBarProps) {
  const [draftFilters, setDraftFilters] = useState<ActivityLogFilters>(filters)

  useEffect(() => {
    setDraftFilters(filters)
  }, [filters])

  const selectedDateRange = useMemo<[Dayjs, Dayjs] | null>(() => {
    if (!draftFilters.from_date || !draftFilters.to_date) return null

    return [dayjs(draftFilters.from_date), dayjs(draftFilters.to_date)]
  }, [draftFilters.from_date, draftFilters.to_date])

  // The backend sends {id, name}; antd wants {value, label}, and the value has to be
  // a string because that is what comes back in the query string.
  const eventOptions = useMemo(
    () =>
      (filterOptions.events || []).map((event) => ({
        label: event.name,
        value: String(event.id),
      })),
    [filterOptions.events],
  )

  // The admin list spans every event, so it is empty only on an install with no events
  // at all — and a select with nothing in it is noise. The event portal supplies no
  // options by design and never sets showEventFilter.
  const eventFilterVisible = showEventFilter && eventOptions.length > 0

  const exportRange = useMemo(
    () => validateExportRange(draftFilters.from_date, draftFilters.to_date),
    [draftFilters.from_date, draftFilters.to_date],
  )

  const submit = (next: ActivityLogFilters) => {
    router.get(basePath, activityLogQueryParams(next, { per_page: perPage }), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const applyFilters = (overrides: ActivityLogFilters = {}) => {
    const next = { ...draftFilters, ...overrides }
    setDraftFilters(next)
    submit(next)
  }

  const clearFilters = () => {
    setDraftFilters({})
    submit({})
  }

  const updateDraft = (patch: ActivityLogFilters) => {
    setDraftFilters((current) => ({ ...current, ...patch }))
  }

  const handleExport = () => {
    if (!exportRange.valid) return

    const query = new URLSearchParams(activityLogQueryParams(draftFilters)).toString()
    window.location.href = query ? `${basePath}/export?${query}` : `${basePath}/export`
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg bg-white p-4 ${className}`}>
      <TableSearch
        value={draftFilters.search || ''}
        onChange={(value) => updateDraft({ search: value })}
        onSearch={() => applyFilters()}
        onReset={() => applyFilters({ search: '' })}
        placeholder="Search actor or target"
        showFilter={false}
      />

      <RangePicker
        value={selectedDateRange}
        format="YYYY-MM-DD"
        placeholder={['From date', 'To date']}
        className="w-full sm:w-[260px]"
        onChange={(dates) => {
          updateDraft({
            from_date: dates?.[0]?.format('YYYY-MM-DD'),
            to_date: dates?.[1]?.format('YYYY-MM-DD'),
          })
        }}
      />

      {eventFilterVisible && (
        <div className="w-full sm:w-56">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All events"
            value={draftFilters.event_id ? String(draftFilters.event_id) : undefined}
            options={eventOptions}
            onChange={(value?: string) => updateDraft({ event_id: value })}
          />
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        <Button variant="secondary" onClick={clearFilters}>
          Clear
        </Button>
        <Button variant="primary" onClick={() => applyFilters()}>
          Apply Filters
        </Button>
        <Tooltip title={exportRange.reason}>
          <span>
            <Button
              variant="secondary"
              icon={<DownloadOutlined />}
              disabled={!exportRange.valid}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  )
}

export default ActivityLogFilterBar
